import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from './ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const ShareSummaryButton = () => {
  const { getAuthHeader } = useAuth();
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState('90');
  const [preparedFor, setPreparedFor] = useState('');
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ days, prepared_for: preparedFor.trim() });
      const res = await axios.get(`${API_URL}/reports/store-summary-pdf?${params}`, {
        headers: getAuthHeader(), responseType: 'blob', timeout: 120000
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Wellness_Store_POS_Summary_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Store summary PDF downloaded');
      setOpen(false);
    } catch (e) {
      toast.error('Could not build the PDF. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="share-summary-button">
          <FileText className="w-4 h-4 mr-2" /> Share Summary PDF
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="share-summary-dialog">
        <DialogHeader>
          <DialogTitle>Store Summary PDF</DialogTitle>
          <DialogDescription>
            A shareable overview of what the system does, with live numbers for every store and screenshots.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Sales period</Label>
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger data-testid="share-summary-days"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="180">Last 6 months</SelectItem>
                <SelectItem value="365">Last 12 months</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Prepared for (optional)</Label>
            <Input
              value={preparedFor}
              onChange={(e) => setPreparedFor(e.target.value)}
              placeholder="e.g. Highbury store owner"
              maxLength={80}
              data-testid="share-summary-prepared-for"
            />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={download} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="share-summary-download">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
            {busy ? 'Building PDF...' : 'Download PDF'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
